import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export function useApi(path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    if (!path) return;
    setLoading(true);
    api.get(path).then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [reload, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, loading, error, reload };
}

export async function act(promise, okMsg) {
  try {
    const r = await promise;
    if (okMsg) toast.success(okMsg);
    return r.data;
  } catch (e) {
    const d = e?.response?.data?.detail;
    toast.error(typeof d === "string" ? d : JSON.stringify(d || e.message));
    throw e;
  }
}

const STATE_COLORS = {
  draft: "bg-neutral-100 text-neutral-700", previewed: "bg-sky-50 text-sky-800", tested: "bg-indigo-50 text-indigo-800",
  pending_approval: "bg-amber-50 text-amber-800", approved: "bg-emerald-50 text-emerald-800", scheduled: "bg-violet-50 text-violet-800",
  published: "bg-[#5A1A22] text-white", rejected: "bg-rose-50 text-rose-800", active: "bg-emerald-50 text-emerald-800",
  paused: "bg-amber-50 text-amber-800", success: "bg-emerald-50 text-emerald-800", failed: "bg-rose-50 text-rose-800",
  dead: "bg-rose-50 text-rose-800", replayed: "bg-emerald-50 text-emerald-800", posted: "bg-emerald-50 text-emerald-800",
  pending: "bg-amber-50 text-amber-800", reversed: "bg-neutral-200 text-neutral-600", provisional: "bg-sky-50 text-sky-800",
  merged: "bg-emerald-50 text-emerald-800", rolled_back: "bg-neutral-200 text-neutral-700", mock: "bg-amber-50 text-amber-800",
  live: "bg-emerald-50 text-emerald-800", disabled: "bg-neutral-200 text-neutral-600", running: "bg-sky-50 text-sky-800",
  waiting: "bg-amber-50 text-amber-800", completed: "bg-emerald-50 text-emerald-800", goal_reached: "bg-[#B08D57] text-white",
  opt_in: "bg-emerald-50 text-emerald-800", opt_out: "bg-rose-50 text-rose-800", suppressed: "bg-rose-50 text-rose-800",
};

export function Pill({ value, testid }) {
  const v = String(value || "");
  return (
    <span data-testid={testid} className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-semibold rounded-sm ${STATE_COLORS[v] || "bg-neutral-100 text-neutral-700"}`}>
      {v.replace(/_/g, " ")}
    </span>
  );
}

export function Panel({ title, eyebrow, right, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`bg-white border border-black/10 rounded-sm ${className}`}>
      {(title || right) && (
        <header className="flex items-center justify-between px-5 py-3 border-b border-black/10">
          <div>
            {eyebrow && <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{eyebrow}</div>}
            <h2 className="font-display text-base md:text-lg">{title}</h2>
          </div>
          <div className="flex items-center gap-2">{right}</div>
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Btn({ children, variant = "primary", className = "", ...props }) {
  const base = "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] rounded-sm transition-[background-color,transform,opacity] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed";
  const v = {
    primary: "bg-[#141414] text-white hover:bg-[#5A1A22]",
    accent: "bg-[#5A1A22] text-white hover:bg-[#3E1017]",
    gold: "bg-[#B08D57] text-white hover:bg-[#8f7145]",
    ghost: "border border-black/15 text-neutral-800 hover:bg-neutral-100",
    danger: "border border-rose-300 text-rose-800 hover:bg-rose-50",
  }[variant];
  return <button className={`${base} ${v} ${className}`} {...props}>{children}</button>;
}

export function Field({ label, children, hint }) {
  return (
    <label className="block text-xs">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-neutral-400 mt-1">{hint}</span>}
    </label>
  );
}

export const inputCls = "w-full border border-black/15 rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#5A1A22]";

export function JsonEditor({ value, onChange, rows = 14, testid }) {
  const [text, setText] = useState(JSON.stringify(value ?? {}, null, 2));
  const [err, setErr] = useState(null);
  useEffect(() => { setText(JSON.stringify(value ?? {}, null, 2)); setErr(null); }, [value]);
  return (
    <div>
      <textarea data-testid={testid} rows={rows} value={text} spellCheck={false}
        className={`${inputCls} font-mono text-[12px] leading-relaxed ${err ? "border-rose-400" : ""}`}
        onChange={(e) => {
          setText(e.target.value);
          try { onChange(JSON.parse(e.target.value)); setErr(null); } catch (x) { setErr(x.message); }
        }} />
      {err && <div className="text-[11px] text-rose-700 mt-1">Invalid JSON: {err}</div>}
    </div>
  );
}

export function Table({ cols, rows, empty = "Nothing here yet", testid, rowKey = "id" }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 border-b border-black/10">
            {cols.map((c) => <th key={c.key} className="py-2 pr-3 font-medium">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length === 0) && <tr><td colSpan={cols.length} className="py-6 text-center text-neutral-400">{empty}</td></tr>}
          {(rows || []).map((r, i) => (
            <tr key={`${r[rowKey] ?? ""}#${i}`} className="border-b border-black/5 hover:bg-[#F6F3EC]/60">
              {cols.map((c) => <td key={c.key} className="py-2 pr-3 align-top">{c.render ? c.render(r) : String(r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Stat({ label, value, hint, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">{label}</div>
      <div className="font-display text-2xl tabular-nums">{value ?? "—"}</div>
      {hint && <div className="text-[11px] text-neutral-400 mt-1">{hint}</div>}
    </div>
  );
}

export const fmtTs = (s) => (s ? new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");

export function Reasons({ reasons }) {
  return (
    <ul className="space-y-1">
      {(reasons || []).map((r, i) => (
        <li key={i} className="flex gap-2 text-xs">
          <code className="shrink-0 px-1.5 py-0.5 bg-[#F6F3EC] border border-black/10 rounded-sm text-[10px] font-semibold">{r.code}</code>
          <span className="text-neutral-700">{r.detail}</span>
        </li>
      ))}
    </ul>
  );
}
